import { z } from "zod";
import { VALID_TERRITORY_CODES } from "@libs/utils";

/**
 * Wire shape for `user.me`. Single source of truth used by:
 *   • `services/user/me.ts` — `.output(userMeWireSchema)` validates server output.
 *   • `apps/mobile/hooks/trpc/useMe/queryFunctions.ts` — parses cached SQLite rows
 *     so a wire-shape change purges old rows automatically (see
 *     `apps/mobile/utils/durableCache`).
 *
 * Drift is impossible — both ends import this schema instance.
 *
 * `consumer` is non-nullable: `services/user/me.ts:86-89` always returns it
 * (even for free users, where `subscription = "free"`). A previous build
 * marked it `.nullable()`, which was over-permissive and let stale-but-now-
 * non-null cache rows survive.
 */
export const userMeWireSchema = z.object({
  blurhash: z.string().nullable(),
  defaultAvatarColors: z.tuple([z.string(), z.string()]).nullable().optional(),
  // keeping consumer for backwards compatibility
  consumer: z.object({
    id: z.string(),
    subscription: z.enum(["free", "premium", "premium-trial"]),
  }),
  // keeping countryCode for backwards compatibility
  countryCode: z.enum(VALID_TERRITORY_CODES),
  plan: z.object({
    type: z.enum(["free", "premium"]),
    currency: z.string(),
    currencySymbol: z.string(),
    grossMonthly: z.number(),
    grossTotal: z.number(),
    numberOfMonths: z.number(),
    platform: z.enum(["ios", "android", "web"]),
    countryId: z.enum(VALID_TERRITORY_CODES),
  }),
  email: z.string(),
  fcmToken: z.string().nullable(),
  apnToken: z.string().nullable(),
  flags: z.object({
    welcome_seen: z.boolean().optional(),
    paywall_seen: z.boolean().optional(),
  }),
  id: z.string(),
  image: z.string().nullable(),
  name: z.string(),
  raffleTickets: z.number().nullable(),
  onboarded: z.boolean(),
  onboardingStep: z.enum([
    "intro_pending",
    "email_verification_pending",
    "setup_pending",
    "welcome_pending",
    "welcome_import_pending",
    "completed",
  ]),
  tipsShown: z.array(z.string()),
  newUpdates: z.object({
    available: z.boolean(),
    showBanner: z.boolean().optional(),
    forceUpdate: z.boolean().optional(),
  }),
  createdAtUnix: z.number(),
  lissenSolanaWalletBase58: z.string().optional(),
});

export type UserMeWire = z.infer<typeof userMeWireSchema>;

/**
 * Wire shape for `user.update` input. Imported by both:
 *   • `apps/api/services/user/update.ts` — `.input(updateUserInputSchema)` on the trpc procedure.
 *   • `apps/mobile/components/Account/Profile/LissenerProfileScreen.tsx` — used for typing the partial-update payload before the trpc call.
 */
export const updateUserInputSchema = z.object({
  name: z.string().optional(),
  image: z.string().optional(),
  email: z.string().optional(),
  flags: z
    .object({
      welcome_seen: z.boolean().optional(),
      paywall_seen: z.boolean().optional(),
    })
    .optional(),
  expoPushToken: z.string().optional().nullable(),
  fcmToken: z.string().optional().nullable(),
  apnToken: z.string().optional().nullable(),
  referredBy: z.string().optional(),
  subscription: z.enum(["free", "premium"]).optional(),
});

export type UpdateUserInput = z.infer<typeof updateUserInputSchema>;
